import { appendAql, removeAql, replaceAql } from '../utils/aqlStringUtils'
import { mapAllFiltersToAQLFields } from '../utils/mapFiltersToAQLFields'
import { targetedAlphanumericFieldPattern, targetedKeyNotFieldPattern } from '../../regexp'
import { SearchOperators, type NotOperator } from '../../enums'
import type { NotOperatorFilterKey } from '../utils/mapFiltersToAQLFields'
import type { AllDashboardCardFilters, SearchOperatorConfig } from '../../types'
import { regexExecAll } from '../utils/regexExecAll'
import { regexpCaptureGroupsToSearchOperatorConfig } from '../utils/searchOperatorConfig'
import { RepTypesValuesEnum } from '../../Views/selectOptions'

// "sellerId" | "sellerType" | "buyerId" | "buyerType"
export const parseNotOperatorField = (
    aql: string,
    searchKey: Exclude<NotOperatorFilterKey, 'rep'>,
): Partial<AllDashboardCardFilters> => {
    const emptyConfig = { [searchKey]: '', [`${searchKey}Op`]: '' }

    if (!aql) {
        return emptyConfig
    }

    const aqlFields = mapAllFiltersToAQLFields[searchKey] || []

    if (!aqlFields?.length) {
        return {}
    }

    // Each member of the union has signatures, but none of those signatures are compatible with a string[]
    const allMatches = (aqlFields as unknown as string[]).reduce<RegExpExecArray[]>((acc, field) => {
        if (acc.length) {
            return acc
        }

        const pattern = targetedKeyNotFieldPattern(field)
        const regex = new RegExp(pattern, 'gi')
        const maybeMatches = regexExecAll(aql, regex)

        return maybeMatches?.length ? maybeMatches : []
    }, [])

    if (!allMatches.length) {
        return emptyConfig
    }

    const firstConfig = allMatches.reduce<SearchOperatorConfig | null>((acc, match) => {
        if (acc) {
            return acc
        }

        const config = regexpCaptureGroupsToSearchOperatorConfig(match.groups)

        if (!config.operator || !config.lowValue) {
            return null
        }

        return config
    }, null)

    if (!firstConfig) {
        return emptyConfig
    }

    const { operator, lowValue } = firstConfig

    return operator && lowValue
        ? {
              [searchKey]: lowValue,
              [`${searchKey}Op`]: operator,
          }
        : emptyConfig
}

export const updateNotOperatorField = (
    prior: string,
    searchKey: Exclude<NotOperatorFilterKey, 'rep'>,
    operator: NotOperator | '' = '',
    value = '',
): string => {
    const aqlFields = mapAllFiltersToAQLFields[searchKey] || []
    const primaryAqlField = aqlFields[0]

    if (!primaryAqlField) {
        return prior
    }
    // Each member of the union has signatures, but none of those signatures are compatible with a string[]
    const preExistingAQLField = (aqlFields as unknown as string[]).reduce((acc, searchField) => {
        if (acc) {
            return acc
        }

        const pattern = targetedAlphanumericFieldPattern(searchField)
        const regex = new RegExp(pattern, 'gi')
        const match = regex.exec(prior)
        return match?.groups?.key || ''
    }, '')

    if (!value || !operator) {
        return preExistingAQLField ? removeAql(prior, preExistingAQLField) : prior
    }

    if (!preExistingAQLField) {
        const searchKeyWithNot = operator === SearchOperators.NOT_EQUAL_TO ? (`${searchKey}Not` as const) : searchKey
        return appendAql(prior, searchKeyWithNot, value)
    }

    if (preExistingAQLField.toLowerCase().includes('not')) {
        const updatedSearchKey =
            operator === SearchOperators.NOT_EQUAL_TO ? preExistingAQLField : preExistingAQLField.replace(/not/i, '')

        return replaceAql(prior, preExistingAQLField, updatedSearchKey, value)
    }

    const updatedSearchKey =
        operator === SearchOperators.NOT_EQUAL_TO ? (`${preExistingAQLField}Not` as const) : preExistingAQLField

    return replaceAql(prior, preExistingAQLField, updatedSearchKey, value)
}
